import { View, Text, Pressable, StyleSheet } from "react-native";

interface PaginationComponentProps {
  currentPage: number;
  totalPages: number;
  handlePageChange: (page: number) => void;
}

/**
 * A component that renders a pagination navigation bar.
 *
 * This component renders a previous button, a next button, and
 * page numbers in between. It will only render up to 3 page numbers
 * at a time, centered around the current page. The previous and next
 * buttons are disabled when the user is at the first or last page.
 *
 * @param {number} currentPage - the current page the user is on
 * @param {number} totalPages - the total number of pages
 * @param {(page: number) => void} handlePageChange - a function to call when the user navigates to a different page
 * @returns {JSX.Element} a pagination navigation bar
 */
const PaginationComponent: React.FC<PaginationComponentProps> = ({
  currentPage,
  totalPages,
  handlePageChange,
}) => {
  const getPaginationNumbers = () => {
    const maxPagesToShow = 5;
    const pages = [];
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <View style={styles.paginationContainer}>
      {/* Previous Button */}
      <Pressable
        onPress={() => currentPage > 1 && handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={[
          styles.prevNextContainer,
          { backgroundColor: currentPage === 1 ? "#a0aec0" : "#39BEB7" },
        ]}
      >
        <Text style={styles.navigationButtonText}>Previous</Text>
      </Pressable>

      {/* Page Numbers */}
      {getPaginationNumbers().map((page, index) => (
        <Pressable
          key={index}
          onPress={() => typeof page === "number" && handlePageChange(page)}
          style={[
            styles.numberPageContainer,
            {
              backgroundColor: currentPage === page ? "#39BEB7" : undefined,
            },
          ]}
        >
          <Text style={styles.numberPageText}>{page}</Text>
        </Pressable>
      ))}

      {/* Next Button */}
      <Pressable
        onPress={() =>
          currentPage < totalPages && handlePageChange(currentPage + 1)
        }
        disabled={currentPage === totalPages}
        style={[
          styles.prevNextContainer,
          {
            backgroundColor: currentPage === totalPages ? "#a0aec0" : "#39BEB7",
          },
        ]}
      >
        <Text style={styles.navigationButtonText}>Next</Text>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  paginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    paddingVertical: 10,
    width: "100%",
  },

  numberPageContainer: {
    justifyContent: "center",
    alignItems: "center",
    height: 40,
    padding: 10,
    margin: 5,
    borderRadius: 5,
  },

  numberPageText: {
    color: "#FFF",
    fontFamily: "PlusJakartaSans-Regular",
  },

  prevNextContainer: {
    justifyContent: "center",
    alignItems: "center",
    height: 40,
    padding: 10,
    backgroundColor: "#39BEB7",
    margin: 5,
    borderRadius: 5,
  },

  navigationButtonText: {
    color: "#FFF",
    fontFamily: "PlusJakartaSans-Regular",
    textAlignVertical: "bottom",
  },
});

export default PaginationComponent;
